import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { getSingleBlog } from "../services/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { motion } from "framer-motion";
import { FaArrowLeft, FaCopy, FaCheck } from "react-icons/fa";

const categoryColors = {
  "System Design": "text-violet-400 bg-violet-500/10 border-violet-500/20",
  "DSA": "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
  "Frontend": "text-cyan-400 bg-cyan-500/10 border-cyan-500/20",
  "Backend": "text-orange-400 bg-orange-500/10 border-orange-500/20",
};

const CopyButton = ({ code }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [code]);
  return (
    <button onClick={handleCopy} className="absolute top-3 right-3 p-1.5 rounded-md bg-gray-700/60 hover:bg-gray-600/80 text-gray-400 hover:text-white transition-all text-xs" title="Copy code">
      {copied ? <FaCheck className="text-emerald-400" /> : <FaCopy />}
    </button>
  );
};

const BlogDetails = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await getSingleBlog(id);
        if (response && response.content) {
          setBlog(response);
        } else {
          setError("Blog not found.");
        }
      } catch (err) {
        setError("Error fetching blog.");
      }
      setLoading(false);
    };
    fetchBlog();
  }, [id]);

  if (loading) return (
    <div className="flex justify-center items-center h-60">
      <div className="w-6 h-6 border-2 border-violet-400/40 border-t-violet-400 rounded-full animate-spin"></div>
    </div>
  );
  if (error) return <div className="text-center text-red-400 py-20 text-sm">{error}</div>;
  if (!blog) return null;

  const readingTime = Math.max(1, Math.ceil((blog.content?.split(/\s+/).length || 0) / 200));
  const formattedDate = new Date(blog.createdAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  return (
    <div className="relative text-white min-h-screen">
      <div className="noise-overlay"></div>

      {/* Ambient glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-violet-600/6 rounded-full blur-[150px] animate-breathe"></div>
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-6 py-12">
        {/* Back link */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}>
          <Link to="/blog" className="inline-flex items-center gap-2 text-xs font-mono text-gray-600 hover:text-gray-300 transition-colors mb-10 group">
            <FaArrowLeft className="text-[10px] group-hover:-translate-x-1 transition-transform" />
            back to articles
          </Link>
        </motion.div>

        {/* Article header */}
        <motion.header initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          {/* Meta row */}
          <div className="flex items-center gap-3 mb-5">
            <span className={`px-2.5 py-1 text-[10px] font-mono rounded-full border ${categoryColors[blog.category] || "text-gray-400 bg-gray-800/40 border-gray-700/50"}`}>
              {blog.category}
            </span>
            <span className="text-[10px] text-gray-700 font-mono">{readingTime} min read</span>
            <span className="text-gray-800">·</span>
            <span className="text-[10px] text-gray-700 font-mono">{formattedDate}</span>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-[1.1] mb-6">
            {blog.title}
          </h1>

          {/* Divider */}
          <div className="w-12 h-px bg-gradient-to-r from-violet-500 to-pink-500 mb-8"></div>
        </motion.header>

        {/* Hero image */}
        {blog.images?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6 }} className="mb-10">
            <div className="rounded-xl overflow-hidden border border-gray-800/50">
              <img src={blog.images[0]} alt={blog.title} className="w-full object-cover" loading="lazy" />
            </div>
          </motion.div>
        )}

        {/* Article body */}
        <motion.article initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.6 }}
          className="article-content"
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              h1: ({ children }) => <h1 className="text-3xl font-black text-white mt-12 mb-4 tracking-tight">{children}</h1>,
              h2: ({ children }) => <h2 className="text-2xl font-bold text-white mt-10 mb-3 tracking-tight">{children}</h2>,
              h3: ({ children }) => <h3 className="text-xl font-bold text-white mt-8 mb-3">{children}</h3>,
              h4: ({ children }) => <h4 className="text-lg font-semibold text-gray-200 mt-6 mb-2">{children}</h4>,
              p: ({ children }) => <p className="text-gray-300 leading-[1.85] mb-5 text-[15px] font-light">{children}</p>,
              a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-violet-400 hover:text-violet-300 underline underline-offset-2 decoration-violet-500/30 hover:decoration-violet-400/60 transition-colors">{children}</a>,
              strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
              em: ({ children }) => <em className="text-gray-400 italic">{children}</em>,
              code({ inline, className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || "");
                const codeString = String(children).replace(/\n$/, "");
                return !inline && match ? (
                  <div className="relative group my-6">
                    <div className="flex items-center justify-between px-4 py-2 bg-[#1e1e2e] border border-gray-800/60 rounded-t-lg">
                      <span className="text-[10px] font-mono text-gray-600">{match[1]}</span>
                    </div>
                    <CopyButton code={codeString} />
                    <SyntaxHighlighter
                      style={oneDark}
                      language={match[1]}
                      customStyle={{ margin: 0, borderTopLeftRadius: 0, borderTopRightRadius: 0, border: "1px solid rgba(55, 65, 81, 0.3)", borderTop: 0, fontSize: "13px", padding: "1.25rem" }}
                      {...props}
                    >
                      {codeString}
                    </SyntaxHighlighter>
                  </div>
                ) : (
                  <code className="bg-violet-500/10 text-violet-300 px-1.5 py-0.5 rounded text-[13px] font-mono">{children}</code>
                );
              },
              img: ({ src, alt }) => (
                <div className="my-8 rounded-xl overflow-hidden border border-gray-800/50">
                  <img src={src} alt={alt} className="w-full object-cover" loading="lazy" />
                  {alt && <p className="text-center text-xs text-gray-600 font-mono py-2 bg-gray-900/40">{alt}</p>}
                </div>
              ),
              blockquote: ({ children }) => (
                <blockquote className="border-l-2 border-violet-500/40 pl-5 my-6 text-gray-400 italic">{children}</blockquote>
              ),
              ul: ({ children }) => <ul className="space-y-2 mb-5 ml-1">{children}</ul>,
              ol: ({ children }) => <ol className="space-y-2 mb-5 ml-1 list-decimal list-inside">{children}</ol>,
              li: ({ children }) => (
                <li className="text-gray-300 text-[15px] font-light leading-relaxed pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-1.5 before:h-1.5 before:rounded-full before:bg-violet-500/40">
                  {children}
                </li>
              ),
              hr: () => <hr className="border-gray-800/50 my-10" />,
              table: ({ children }) => (
                <div className="overflow-x-auto my-6 rounded-lg border border-gray-800/50">
                  <table className="w-full text-sm">{children}</table>
                </div>
              ),
              thead: ({ children }) => <thead className="bg-gray-800/40 text-gray-400">{children}</thead>,
              th: ({ children }) => <th className="px-4 py-2.5 text-left text-xs font-mono uppercase tracking-wider">{children}</th>,
              td: ({ children }) => <td className="px-4 py-2.5 text-gray-300 border-t border-gray-800/40 text-[13px]">{children}</td>,
            }}
          >
            {blog.content}
          </ReactMarkdown>
        </motion.article>

        {/* Additional images */}
        {blog.images?.length > 1 && (
          <div className="mt-10 space-y-4">
            {blog.images.slice(1).map((img, i) => (
              <div key={i} className="rounded-xl overflow-hidden border border-gray-800/50">
                <img src={img} alt={`${blog.title} ${i + 2}`} className="w-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-gray-800/40">
          <Link to="/blog" className="inline-flex items-center gap-2 text-xs font-mono text-gray-600 hover:text-gray-300 transition-colors group">
            <FaArrowLeft className="text-[10px] group-hover:-translate-x-1 transition-transform" />
            back to all articles
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BlogDetails;
